<template>
  <div class="opus-absence text-dark">
    <h2 class="marg-1-bottom capitalize">Fraværsindberetninger</h2>
    <template v-if="isNoConfiguration">
      <div class="opus-absence-no-config">
        <h2>
          <i>Skolens Opus-integration er ikke konfigureret korrekt. Kontakt Docendo for at få hjælp til opsætningen -
            enten på chat eller email.</i>
        </h2>
      </div>
    </template>
    <template v-else>
      <div class="opus-absence__header">
        <div class="opus-absence__header__left">
          <DoDatePickerRange
            :allowSameDate="true"
            :startDate="startDate"
            :endDate="endDate"
            :maximumDateRange="365"
            @startChanged="startDateChanged"
            @endChanged="endDateChanged">
          </DoDatePickerRange>
          <Tabs
            :tabs="tabsList"
            :selected="selectedTab"
            @select="selectedTab = $event">
          </Tabs>
        <DoFiltersFilter
          :title="'Personer'"
          :tags="uniqueNames"
          :selectedTags="selectedNames"
          :numberOfSelectedTagsToShow="2"
          :placeholder="'Vælg personer'"
          @select="toggleName"
          @clear="clearNames">
        </DoFiltersFilter>
        <DoFiltersFilter
          :title="'Fraværstype'"
          :tags="uniqueAbsenceTypes"
          :selectedTags="selectedAbsenceTypes"
          :numberOfSelectedTagsToShow="2"
          :placeholder="'Vælg fraværstype'"
          @select="toggleAbsenceType"
          @clear="clearAbsenceTypes">
        </DoFiltersFilter>
        </div>
        <div class="opus-absence__header__right" v-if="isOpusAbsenceActivated">
          <button class="do-bttn" @click="isShowQuotasModal = true">Vis kvoter</button>
        </div>
      </div>

      <div class="opus-absence__body">
        <Loader v-if="isLoading"></Loader>

        <template v-else>
          <table class="do-table">
            <thead>
            <tr>
              <th
                class="th-sorter td-created"
                @click="setSort('vCreatedAt')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': !isSortPristine && sortBy==='vCreatedAt'}">
                Oprettet <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter"
                @click="setSort('name')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='name'}">
                Navn <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter"
                @click="setSort('employment_number')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='employment_number'}">
                Ansættelse <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter"
                @click="setSort('absence_type_text')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='absence_type_text'}">
                Fraværstype <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter td-date"
                @click="setSort('start_date')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='start_date'}">
                Startdato <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter td-date"
                @click="setSort('end_date')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='end_date'}">
                Slutdato <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter td-units"
                @click="setSort('absence_days')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='absence_days'}">
                Dage <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter td-units"
                @click="setSort('absence_hours')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': sortBy==='absence_hours'}">
                Timer <span class="icon-menu-open"></span>
              </th>

              <th
                class="th-sorter td-status"
                @click="setSort('state')"
                role="button"
                tabindex="0"
                :class="{'th-sorter--active': !isSortPristine && sortBy==='state'}">
                Status <span class="icon-menu-open"></span>
              </th>

              <th v-if="selectedTab !== 'queued'" class="td-error">
                Besked
              </th>

              <th class="td-action"></th>

            </tr>
            </thead>
            <tbody>
            <tr v-if="displayedAbsences.length === 0">
              <td :colspan="selectedTab !== 'queued' ? 11 : 10" class="italic">
                Ingen fraværsindberetninger fundet
              </td>
            </tr>
            <tr v-for="item in displayedAbsences"
                :key="item.id">
              <td class="td-created">{{ item.vCreatedAt }}</td>
              <td>{{ item.vUserName }}</td>
              <td>{{ item.vEmploymentNumber }}</td>
              <td>{{ item.vAbsenceTypeText }}</td>
              <td class="td-date">{{ item.vStartDate }}</td>
              <td class="td-date">{{ item.ongoing ? 'Åbenstående' : item.vEndDate }}</td>
              <td class="td-units">{{ formatDecimal(item.absence_days) }}</td>
              <td class="td-units">{{ formatDecimal(item.absence_hours) }}</td>
              <td class="td-status">
                <span v-if="item.state === 'error'" class="status-pill status-pill--error">Fejlet</span>
                <span v-else-if="item.state === 'queued' || !item.state" class="status-pill status-pill--pending">Afventer</span>
                <span v-else-if="item.state === 'ok'" class="status-pill status-pill--ok">Gennemført</span>
                <span v-else class="status-pill status-pill--pending">Afventer</span>
              </td>
              <td v-if="selectedTab !== 'queued'" class="td-error">
                <div class="message-list" v-if="item.error && item.error.length > 0">
                  <span v-for="(err, idx) in item.error"
                        :key="idx"
                        :class="{'message-error': err.StatusType === 'E', 'message-warning': err.StatusType === 'W'}">
                    {{ err.MessageText }}
                  </span>
                </div>
              </td>
              <td class="td-action">
                <div class="td-action__buttons">
                  <button
                    v-if="isOpusAbsenceActivated && item.employment_id"
                    class="do-bttn do-bttn--small"
                    :disabled="!isOpusEmployment(item.employment)"
                    :title="isOpusEmployment(item.employment) ? 'Se personens fravær, som det er registreret i KMD Opus' : 'Ansættelsen er ikke en Opus-ansættelse'"
                    @click="openKmdAbsence(item)">
                    <span>Se i Opus</span>
                  </button>
                  <button
                    v-if="item.state === 'error'"
                    class="do-bttn do-bttn--small"
                    :class="{'do-bttn--loading': item.isRetrying}"
                    :disabled="item.isRetrying"
                    @click="retrySend(item)">
                    <span>Send igen</span>
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
    </template>
    <CreateOpusAbsence
      v-if="isShowCreateOpusAbsence"
      :schoolId="schoolId"
      @created="onCreated($event)"
      @close="setIsShowCreateOpusAbsence(false)">
    </CreateOpusAbsence>
    <KmdQuotasModal
      v-if="isShowQuotasModal"
      @close="isShowQuotasModal = false">
    </KmdQuotasModal>
    <KmdOpusAbsenceModal
      v-if="kmdAbsenceItem"
      :employmentId="kmdAbsenceItem.employment_id"
      :userName="String(kmdAbsenceItem.vUserName || '')"
      :employmentLabel="String(kmdAbsenceItem.vEmploymentNumber || '')"
      :transfer="kmdAbsenceItem"
      :absenceTypeCode="getAbsenceTypeCode(kmdAbsenceItem.kmd_absence_code_id)"
      @close="kmdAbsenceItem = null">
    </KmdOpusAbsenceModal>
  </div>
</template>

<script>
import docendo from '../../../docendo'
import eventbus from '../../../eventbus'
import Loader from '../../common/Loader.vue'
import moment from 'moment'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
import CreateOpusAbsence from './CreateOpusAbsence.vue'
import KmdQuotasModal from './KmdQuotasModal.vue'
import KmdOpusAbsenceModal from './KmdOpusAbsenceModal.vue'
import DoDatePickerRange from '../../datepicker/DoDatePickerRange.vue'
import Tabs from '../../common/Tabs.vue'

export default {
  name: 'OpusAbsence',
  components: {Loader, DoFiltersFilter, CreateOpusAbsence, KmdQuotasModal, KmdOpusAbsenceModal, DoDatePickerRange, Tabs},
  data () {
    return {
      isNoConfiguration: false,
      isLoading: false,
      opusAbsence: [],
      // Default: sorter efter oprettelsesdato (nyeste først)
      sortBy: 'vCreatedAt',
      sortDir: 'desc',
      isSortPristine: true,
      selectedNames: [],
      selectedAbsenceTypes: [],
      isShowCreateOpusAbsence: false,
      isShowQuotasModal: false,
      startDate: moment().startOf('month').startOf('day'),
      endDate: moment().endOf('month').endOf('day'),
      absenceCodes: [],
      selectedTab: 'all',
      kmdAbsenceItem: null
    }
  },
  computed: {
    tabsList () {
      const allCount = this.opusAbsenceSorted.length
      const failedCount = this.failedCount
      const queuedCount = this.queuedCount
      const successCount = this.successCount
      return [
        {
          id: 'all',
          title: `Alle (${allCount})`
        },
        {
          id: 'failed',
          title: `Fejlede (${failedCount})`
        },
        {
          id: 'queued',
          title: `Afventer (${queuedCount})`
        },
        {
          id: 'success',
          title: `Gennemførte (${successCount})`
        }
      ]
    },
    successCount () {
      return this.opusAbsenceSorted.filter(x => x.state === 'ok').length
    },
    failedCount () {
      return this.opusAbsenceSorted.filter(x => x.state === 'error').length
    },
    queuedCount () {
      return this.opusAbsenceSorted.filter(x => x.state === 'queued' || !x.state || x.state === '').length
    },
    metaKey () {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      return isMac ? '⌘' : 'CTRL'
    },
    saveKeyTooltip () {
      return '<p class="tooltip-shortcut tooltip-shortcut--double-key"><span>' + this.metaKey + '</span>' + '<span>Enter</span></p>'
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    isOpusAbsenceActivated () {
      return this.$store.getters.schoolFeatures && this.$store.getters.schoolFeatures.indexOf('kmd_absence') > -1
    },
    usersById () {
      const map = {}
      const list = this.$store.getters.usersSortedOnName || []
      for (let i = 0; i < list.length; i++) {
        const u = list[i]
        map[u.id] = u.name
      }
      return map
    },
    employmentsAll () {
      return this.$store.state.employments && this.$store.state.employments.employments
        ? this.$store.state.employments.employments
        : []
    },
    employmentsById () {
      const map = {}
      const list = this.employmentsAll || []
      for (let i = 0; i < list.length; i++) {
        const e = list[i]
        map[e.id] = e
      }
      return map
    },
    opusAbsenceSorted () {
      return this.opusAbsence.map(item => {
        const employment = this.employmentsById[item.employment_id] || null
        const vUserName = employment && employment.user_id
          ? this.getUserName(employment.user_id)
          : (item.employment_number || 'Ukendt')
        const vEmploymentNumber = employment && employment.employment_number
          ? employment.employment_number + (employment.title ? ' - ' + employment.title : '')
          : (item.employment_number || 'Ukendt')
        const vAbsenceTypeText = this.getAbsenceCodeName(item.kmd_absence_code_id)
        const vCreatedAt = item.created_at ? moment(item.created_at).format('lll') : ''
        const vStartDate = item.from_date ? moment(item.from_date).format('DD-MM-YYYY') : ''
        const vEndDate = item.to_date && item.to_date !== '9999-12-31'
          ? moment(item.to_date).format('DD-MM-YYYY')
          : 'Løbende'
        // Beregn antal dage
        let vAbsenceDays = item.absence_days || 0
        let vAbsenceCalendarDays = item.absence_calendar_days || 0
        let vAbsenceHours = item.absence_hours || 0
        if (item.from_date) {
          const startDate = moment(item.from_date)
          const endDate = item.to_date && item.to_date !== '9999-12-31'
            ? moment(item.to_date)
            : moment() // Hvis løbende, brug i dag som slutdato
          // Beregn kalenderdage (inkluderer både start og slutdato)
          vAbsenceCalendarDays = endDate.diff(startDate, 'days') + 1
          // Hvis absence_days ikke er sat, brug kalenderdage
          if (!item.absence_days) {
            vAbsenceDays = vAbsenceCalendarDays
          }
        }
        // Beregn timer hvis from_time og to_time er sat
        if (item.from_time && item.to_time && !item.absence_hours) {
          const startTime = moment(item.from_time)
          const endTime = moment(item.to_time)
          // Beregn timer direkte (moment håndterer flere dage automatisk)
          vAbsenceHours = endTime.diff(startTime, 'hours', true) // true for decimaler
        }
        return Object.assign({}, item, {
          vUserName,
          vEmploymentNumber,
          vAbsenceTypeText,
          vCreatedAt,
          vStartDate,
          vEndDate,
          vAbsenceDays,
          vAbsenceCalendarDays,
          vAbsenceHours,
          absence_days: vAbsenceDays, // Opdater også den originale property for sortering
          absence_calendar_days: vAbsenceCalendarDays, // Opdater også den originale property for sortering
          absence_hours: vAbsenceHours, // Opdater også den originale property for sortering
          employment
        })
      })
    },
    uniqueNames () {
      const set = {}
      for (let i = 0; i < this.opusAbsenceSorted.length; i++) {
        const s = this.opusAbsenceSorted[i]
        const name = s.vUserName || ''
        if (name) set[name] = true
      }
      const arr = Object.keys(set)
      return arr.sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    uniqueAbsenceTypes () {
      const set = {}
      for (let i = 0; i < this.opusAbsenceSorted.length; i++) {
        const s = this.opusAbsenceSorted[i]
        const type = s.vAbsenceTypeText || ''
        if (type) set[type] = true
      }
      const arr = Object.keys(set)
      return arr.sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    displayedAbsences () {
      let rows = this.opusAbsenceSorted.slice()

      // Filtrer på tab (alle/fejlede/afsendes/succes)
      if (this.selectedTab === 'failed') {
        rows = rows.filter(x => x.state === 'error')
      } else if (this.selectedTab === 'success') {
        rows = rows.filter(x => x.state === 'ok')
      } else if (this.selectedTab === 'queued') {
        rows = rows.filter(x => x.state === 'queued' || !x.state || x.state === '')
      }
      // 'all' viser alle, ingen filtrering

      // Filtrer på navne
      if (this.selectedNames && this.selectedNames.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedNames.length; i++) chosen[this.selectedNames[i]] = true
        rows = rows.filter(x => chosen[x.vUserName || ''])
      }

      // Filtrer på fraværstype
      if (this.selectedAbsenceTypes && this.selectedAbsenceTypes.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedAbsenceTypes.length; i++) chosen[this.selectedAbsenceTypes[i]] = true
        rows = rows.filter(x => chosen[x.vAbsenceTypeText || ''])
      }

      const get = (item, key) => {
        switch (key) {
          case 'state':
            // Sortering på status:
            // asc: fejlede -> afventer -> succes m. kommentar (W) -> succes
            // desc: omvendt (håndteres automatisk af sortDir)
            if (item.state === 'error') return 0
            if (item.state === 'queued' || !item.state || item.state === '') return 1
            if (item.state === 'ok') {
              if (item.error && item.error.some(x => x && x.StatusType === 'W')) return 2
              return 3
            }
            return 4
          case 'name':
            return item.vUserName || ''
          case 'employment_number':
            return String(item.vEmploymentNumber || '')
          case 'absence_type_text':
            return String(item.vAbsenceTypeText || '')
          case 'start_date':
            return item.from_date ? moment(item.from_date).toDate() : new Date(0)
          case 'end_date':
            if (!item.to_date || item.to_date === '9999-12-31') {
              return new Date('9999-12-31')
            }
            return moment(item.to_date).toDate()
          case 'absence_days':
            return this._num(item.absence_days)
          case 'absence_hours':
            return this._num(item.absence_hours)
          case 'absence_calendar_days':
            return this._num(item.absence_calendar_days)
          case 'vCreatedAt':
            return this._momentOrNull(item.created_at).valueOf() || 0
          default:
            return item[key] || ''
        }
      }

      const dir = this.sortDir === 'asc' ? 1 : -1
      const key = this.sortBy

      return rows.slice().sort((a, b) => {
        const va = get(a, key)
        const vb = get(b, key)
        if (va === vb) return 0
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        if (va instanceof Date && vb instanceof Date) return (va - vb) * dir
        if (typeof va === 'number' && typeof vb !== 'number') return -1 * dir
        if (typeof vb === 'number' && typeof va !== 'number') return 1 * dir
        return String(va).localeCompare(String(vb), 'da', {sensitivity: 'base'}) * dir
      })
    }
  },
  methods: {
    retrySend (item) {
      this.$set(item, 'isRetrying', true)
      docendo.schoolEventSerialsRetrySend(this.schoolId, item.id, (response) => {
        this.$set(item, 'isRetrying', false)
        this.$set(item, 'state', 'queued')
        this.$set(item, 'error', [])
        eventbus.toast = {text: 'Forsøgt sendt igen', success: false}
        this.readOpusAbsence(false)
      }, () => {
        this.$set(item, 'isRetrying', false)
        eventbus.toast = {text: 'Kunne ikke sende igen', success: false}
      })
    },
    openKmdAbsence (item) {
      this.kmdAbsenceItem = item
    },
    isOpusEmployment (employment) {
      // Ukendt ansættelse (fx ikke indlæst endnu) tillades; backend validerer
      return !employment || !employment.source || employment.source === 'opus'
    },
    getUserName (userId) {
      if (!userId) {
        return ''
      }
      const user = this.usersById[userId]
      if (user) {
        return user
      }
      return userId
    },
    toggleName (name) {
      const idx = this.selectedNames.indexOf(name)
      if (idx >= 0) {
        this.selectedNames.splice(idx, 1)
      } else {
        this.selectedNames.push(name)
      }
    },
    clearNames () {
      this.selectedNames = []
    },
    toggleAbsenceType (type) {
      const idx = this.selectedAbsenceTypes.indexOf(type)
      if (idx >= 0) {
        this.selectedAbsenceTypes.splice(idx, 1)
      } else {
        this.selectedAbsenceTypes.push(type)
      }
    },
    clearAbsenceTypes () {
      this.selectedAbsenceTypes = []
    },
    setSort (key) {
      this.isSortPristine = false
      if (this.sortBy === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = key
        this.sortDir = 'asc'
      }
    },
    _num (v) {
      const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'))
      return Number.isFinite(n) ? n : 0
    },
    _momentOrNull (iso) {
      return iso ? moment(iso) : null
    },
    formatDecimal (value) {
      if (value == null || value === '') return ''
      const num = Number(value)
      if (isNaN(num)) return String(value).replace('.', ',')
      const formatted = num % 1 === 0 ? String(num) : num.toFixed(2)
      return formatted.replace('.', ',')
    },
    setIsShowCreateOpusAbsence (bool) {
      this.isShowCreateOpusAbsence = bool
    },
    onCreated (data) {
      if (!data || !data.id) return
      const idx = this.opusAbsence.findIndex(item => item.id === data.id)
      if (idx < 0) {
        // Ny → læg ind i listen
        this.opusAbsence.push(data)
      } else {
        // Eksisterer → opdater elementet på samme plads (reaktivt)
        this.$set(this.opusAbsence, idx, Object.assign({}, this.opusAbsence[idx], data))
      }
      this.readOpusAbsence()
    },
    readOpusAbsence (isShowSpinner = true) {
      if (isShowSpinner) {
        this.isLoading = true
      }
      const startDate = moment(this.startDate).format('YYYY-MM-DD')
      const endDate = moment(this.endDate).format('YYYY-MM-DD')
      docendo.schoolEventSerialsListKmd(this.schoolId, startDate, endDate, success => {
        if (success && success.status && success.status === 'error') {
          this.isNoConfiguration = true
        }
        if (Array.isArray(success)) {
          this.opusAbsence = success
        } else if (success && Array.isArray(success.data)) {
          this.opusAbsence = success.data
        } else {
          this.opusAbsence = []
        }
        this.isLoading = false
      }, failure => {
        console.log(failure)
        this.isLoading = false
        this.opusAbsence = []
      })
    },
    startDateChanged (date) {
      this.startDate = date
      this.readOpusAbsence()
    },
    endDateChanged (date) {
      this.endDate = date
      this.readOpusAbsence()
    },
    // KMD-fraværskoden (fx 'SY'), som svarer til kmd_absence_attendance_type i Opus
    getAbsenceTypeCode (codeId) {
      if (!codeId || !Array.isArray(this.absenceCodes)) return ''
      const code = this.absenceCodes.find(c => c.id === codeId)
      return code && code.absence_type ? String(code.absence_type) : ''
    },
    getAbsenceCodeName (codeId) {
      if (!codeId || !this.absenceCodes || !Array.isArray(this.absenceCodes)) {
        return ''
      }
      const code = this.absenceCodes.find(c => c.id === codeId)
      if (!code) {
        return ''
      }
      return code.absence_type + (code.absence_type_text ? ' - ' + code.absence_type_text : '')
    },
    readAbsenceCodes () {
      docendo.listKmdOpusAbsenceCodes(this.schoolId, success => {
        this.absenceCodes = success
      }, failure => {
        this.absenceCodes = []
      })
    }
  },
  mounted () {
    this.$store.dispatch('readEmployments')
    this.readAbsenceCodes()
    this.readOpusAbsence()
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.opus-absence {
  padding: 1rem;
  min-width: 1280px;
}

.opus-absence__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.opus-absence__header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.opus-absence__header__right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.do-table__filters {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: #f6f9fc;
  border: 1px solid #e2e8f0;
  border-bottom: 0;
  padding: 8px;
  border-radius: 4px 4px 0 0;
  margin-bottom: 0;
}

.do-table tbody tr:hover {
  background-color: aliceblue !important;
}

.do-table tr:nth-child(2n) {
  background-color: #f9fafb;
}

.do-table td {
  border-left: 1px solid #e2e8f0;
}

.th-sorter {
  cursor: pointer;
}

.th-sorter:hover {
  transition: 0.15s;
}

.th-sorter:active {
  transform: scale(0.98);
}

.th-sorter.th-sorter--active {
  color: var(--primary);
}

.td-date, .td-units {
  width: 100px;
}

.td-units {
  text-align: right;
  width: 80px;
}

.td-created {
  width: 150px;
}

.capitalize {
  text-transform: capitalize;
}

.td-error {
  max-width: 300px;
}

.error-text {
  color: #dc2626;
  font-size: 0.875rem;
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.875rem;
}

.message-error {
  color: #dc2626;
  font-style: italic;
}

.message-warning {
  color: #222222;
  font-style: italic;
}

.td-status {
  width: 100px;
}

.td-action {
  width: 160px;
}

.td-action__buttons {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  white-space: nowrap;
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  line-height: 1.5;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-pill--ok {
  background-color: #dcfce7;
  color: #166534;
}

.status-pill--warning {
  background-color: #fef9c3;
  color: #854d0e;
}

.status-pill--error {
  background-color: #fee2e2;
  color: #dc2626;
}

.status-pill--pending {
  background-color: #f3f4f6;
  color: #6b7280;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.do-bttn--small.do-bttn.do-bttn--loading:before {
  width: 8px; 
  height: 8px; 
}

</style>



// WEBPACK FOOTER //
// OpusAbsence.vue?7a1e5c87